
import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Non-Selling Report Display Component
const NonSellingReportDisplay = ({ filters, branchProfile, nonSellingProducts }) => {
  const reportTitle = 'Non-Selling Products Report';
  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  // Utility function to format date to dd-mm-yy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '24px',
      marginTop: '24px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '14px',
      pageBreakInside: 'avoid',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    }} className="print-only">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
        borderBottom: '2px solid #e2e8f0',
        paddingBottom: '16px',
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0',
            marginBottom: '12px',
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{
            fontSize: '14px',
            lineHeight: '1.5',
            color: '#374151',
          }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>{reportTitle}</strong>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>From Date:</strong> {formatDate(filters.fromDate)}
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>To Date:</strong> {formatDate(filters.toDate)}
            </div>
            <div>
              <strong>Branches:</strong> {filters.branch.includes("All Branches") ? "All Branches" : filters.branch.join(", ")}
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '12px',
          textAlign: 'center',
          minWidth: '120px',
          fontSize: '14px',
          fontWeight: '600',
        }}>
          {city}
        </div>
      </div>

      {/* Non-Selling Products Table */}
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '1px solid #d1d5db',
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f0f9ff', borderBottom: '2px solid #3b82f6' }}>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>S.No</th>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>Product Code</th>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>Product Name</th>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>Stock Available</th>
          </tr>
        </thead>
        <tbody>
          {nonSellingProducts.length === 0 ? (
            <tr>
              <td colSpan={4} style={{
                border: '1px solid #d1d5db',
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
                fontStyle: 'italic',
                fontSize: '14px',
              }}>
                No non-selling products found for the selected criteria
              </td>
            </tr>
          ) : (
            nonSellingProducts.map((product, index) => (
              <tr key={index}>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', color: '#374151' }}>{index + 1}</td>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', color: '#374151' }}>{product.productCode || 'N/A'}</td>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'left', color: '#374151' }}>{product.productName}</td>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'right', color: '#374151' }}>{parseFloat(product.stock || 0).toFixed(2)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Main Sales Report Non-Selling Generator Component
const SalesReportNonSellingGenerator = () => {
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    branch: ["All Branches"]
  });
  const [branches, setBranches] = useState(["All Branches"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [products, setProducts] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [nonSellingProducts, setNonSellingProducts] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
        setBranches(["All Branches", latestProfile.branchName || "SUPREME TRADERS"]);
      }
    });


const productsRef = ref(database, `users/${encodedEmail}/products`);
onValue(productsRef, (snapshot) => {
  const data = snapshot.val();
  if (data) {
    const productList = Object.entries(data).map(([id, product]) => ({
      id,
      productCode: id, // Use the Firebase ID as product code
      productName: product.productName || 'Unknown Product',
      stock: parseFloat(product.openingStock) || 0
    }));
    setProducts(productList);
  } else {
    setProducts([]);
  }
});
    // Load Sales Data
    const salesRef = ref(database, `users/${encodedEmail}/sales`);
    onValue(salesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const salesRecords = Object.entries(data).map(([id, sale]) => ({
          id,
          date: sale.date || 'N/A',
          items: sale.items || []
        }));
        setSalesData(salesRecords);
      } else {
        setSalesData([]);
      }
    });

    return () => {
      off(branchProfileRef);
      off(productsRef);
      off(salesRef);
    };
  };

  // Calculate Non-Selling Products
  useEffect(() => {
    if (!filters.fromDate || !filters.toDate || !products.length || !salesData.length) {
      setNonSellingProducts([]);
      return;
    }

    const fromDate = new Date(filters.fromDate);
    const toDate = new Date(filters.toDate);

    // Filter sales within date range
    const filteredSales = salesData.filter(sale => {
      const saleDate = new Date(sale.date.split('-').reverse().join('-')); // Convert DD-MM-YYYY to YYYY-MM-DD
      return saleDate >= fromDate && saleDate <= toDate;
    });

    // Identify sold products
    const soldProductNames = new Set();
    filteredSales.forEach(sale => {
      sale.items.forEach(item => {
        if (item.itemDescription) {
          soldProductNames.add(item.itemDescription);
        }
      });
    });

    // Filter non-selling products
    const nonSelling = products
      .filter(product => !soldProductNames.has(product.productName) && product.stock > 0)
      .map(product => ({
        productCode: product.productCode,
        productName: product.productName,
        stock: product.stock
      }));

    setNonSellingProducts(nonSelling);
  }, [products, salesData, filters.fromDate, filters.toDate]);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    if (!filters.fromDate || !filters.toDate) {
      alert('Please select both From Date and To Date.');
      return;
    }
    setShowReport(true);
  };

  const handlePrint = () => {
    const printContent = (
      <NonSellingReportDisplay 
        filters={filters}
        branchProfile={branchProfile}
        nonSellingProducts={nonSellingProducts}
      />
    );

    const htmlString = ReactDOMServer.renderToString(printContent);

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Non-Selling Products Report</title>
          <style>
            body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; }
            @page { size: A4; margin: 5mm; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownloadExcel = () => {
    const sheetData = nonSellingProducts.map((product, index) => ({
      'S.No': index + 1,
      'Product Code': product.productCode,
      'Product Name': product.productName,
      'Stock Available': parseFloat(product.stock).toFixed(2)
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Non-Selling Products');
    XLSX.writeFile(workbook, 'NonSellingProductsReport.xlsx');
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFont('Arial');
    doc.setFontSize(12);

    // Header
    doc.text(branchProfile.branchName, 10, 10);
    doc.setFontSize(10);
    doc.text('Non-Selling Products Report', 10, 20);
    doc.text(`From Date: ${filters.fromDate || 'N/A'}`, 10, 30);
    doc.text(`To Date: ${filters.toDate || 'N/A'}`, 10, 40);
    doc.text(`Branches: ${filters.branch.includes("All Branches") ? "All Branches" : filters.branch.join(", ")}`, 10, 50);
    const city = branchProfile.city || getCityFromAddress(branchProfile.address);
    doc.text(city, 180, 10, { align: 'right' });

    // Non-Selling Products Table
    autoTable(doc, {
      startY: 60,
      head: [['S.No', 'Product Code', 'Product Name', 'Stock Available']],
      body: nonSellingProducts.map((product, index) => [
        index + 1,
        product.productCode,
        product.productName,
        parseFloat(product.stock).toFixed(2)
      ]),
      theme: 'grid',
      headStyles: { fillColor: [224, 242, 254], textColor: [0, 0, 0], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 40 },
        2: { cellWidth: 80 },
        3: { cellWidth: 40, halign: 'right' }
      }
    });

    doc.save('NonSellingProductsReport.pdf');
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        fontSize: '16px',
        color: '#6b7280',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        Loading...
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '2rem',
        color: '#6b7280',
        fontSize: '16px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        Please login to access the Non-Selling Products Report 
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>
          <span role="img" aria-label="chart"></span> Non-Selling Products Report 
        </h1>
      </div>
      
      {/* Filter Inputs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            placeholder="From Date"
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            placeholder="To Date"
          />
        </div>
       
      </div>
<div>
<FilterSection 
          title="Branches" 
          options={branches} 
          selected={filters.branch} 
          onChange={(value) => handleFilterChange("branch", value)}
        />
</div>
      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handleDownloadExcel}
          style={{
            background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Download Excel
        </button>
        <button
          onClick={handleDownloadPDF}
          style={{
            background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Download PDF
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          <span role="img" aria-label="print">🖨️</span> Print
        </button>
      </div>

      {/* Non-Selling Report Display */}
      {showReport && (
        <NonSellingReportDisplay 
          filters={filters}
          branchProfile={branchProfile}
          nonSellingProducts={nonSellingProducts}
        />
      )}
    </div>
  );
};

export default SalesReportNonSellingGenerator;